'use client';

import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Bold, Italic, Underline, List, ListOrdered, Link2, Paperclip, X, Eye, Send, Mail } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


interface CampaignFormData {
  name: string;
  subject: string;
  fromName: string;
  fromEmail: string;
  mailingList: string;
  tags: string;
  sendTiming: 'now' | 'scheduled';
  scheduledAt: string;
}

interface AttachedFile {
  id: string;
  name: string;
  size: string;
}

const mailingLists = [
  'All Subscribers',
  'Premium Customers',
  'Active Buyers',
  'VIP Members',
  'New Signups',
  'Inactive 90d',
  'Beta Users',
];

interface Props {
  onClose: () => void;
  onSave: (data: any) => void;
}

export default function CampaignForm({ onClose, onSave }: Props) {
  const { register, handleSubmit, watch, getValues, formState: { errors, isSubmitting } } = useForm<CampaignFormData>({
    defaultValues: { sendTiming: 'now', fromName: 'Marcus Allen', fromEmail: 'marcus@mailflow.io' },
  });
  const sendTiming = watch('sendTiming');
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [bodyHtml, setBodyHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const execFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleInsertLink = () => {
    const url = prompt('Enter URL:');
    if (url) execFormat('createLink', url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newFiles: AttachedFile[] = files.map((f, i) => ({
      id: `file-${Date.now()}-${i}`,
      name: f.name,
      size: f.size > 1024 * 1024
        ? `${(f.size / (1024 * 1024)).toFixed(1)} MB`
        : `${(f.size / 1024).toFixed(0)} KB`,
    }));
    setAttachments((prev) => [...prev, ...newFiles]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((f) => f.id !== id));
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setTestSending(true);
    await new Promise((r) => setTimeout(r, 1200));
    setTestSending(false);
    setTestSent(true);
    setTimeout(() => { setTestSent(false); setTestSendOpen(false); setTestEmail(''); }, 2500);
  };

  const onSubmit = (data: CampaignFormData) => {
    onSave({ ...data, bodyHtml, attachments });
  };

  const formValues = getValues();

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
        {/* Campaign Name */}
        <div>
          <label className="label-base" htmlFor="name">Campaign Name</label>
          <p className="text-xs text-muted-foreground mb-1">Internal name — not visible to recipients</p>
          <input
            id="name"
            type="text"
            placeholder="e.g. August Newsletter #1"
            className="input-base"
            {...register('name', { required: 'Campaign name is required' })}
          />
          {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
        </div>

        {/* Subject */}
        <div>
          <label className="label-base" htmlFor="subject">Email Subject</label>
          <p className="text-xs text-muted-foreground mb-1">The subject line recipients will see in their inbox</p>
          <input
            id="subject"
            type="text"
            placeholder="e.g. Your August update is here 🎉"
            className="input-base"
            {...register('subject', { required: 'Subject is required' })}
          />
          {errors.subject && <p className="text-xs text-red-600 mt-1">{errors.subject.message}</p>}
        </div>

        {/* From Name + Email */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-base" htmlFor="fromName">From Name</label>
            <input
              id="fromName"
              type="text"
              placeholder="Your Name"
              className="input-base"
              {...register('fromName', { required: 'From name is required' })}
            />
            {errors.fromName && <p className="text-xs text-red-600 mt-1">{errors.fromName.message}</p>}
          </div>
          <div>
            <label className="label-base" htmlFor="fromEmail">From Email</label>
            <input
              id="fromEmail"
              type="email"
              placeholder="you@yourdomain.com"
              className="input-base"
              {...register('fromEmail', {
                required: 'From email is required',
                pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
              })}
            />
            {errors.fromEmail && <p className="text-xs text-red-600 mt-1">{errors.fromEmail.message}</p>}
          </div>
        </div>

        {/* Mailing List */}
        <div>
          <label className="label-base" htmlFor="mailingList">Mailing List</label>
          <select
            id="mailingList"
            className="input-base"
            {...register('mailingList', { required: 'Select a mailing list' })}
          >
            <option value="">Select a list…</option>
            {mailingLists.map((l) => (
              <option key={`list-opt-${l}`} value={l}>{l}</option>
            ))}
          </select>
          {errors.mailingList && <p className="text-xs text-red-600 mt-1">{errors.mailingList.message}</p>}
        </div>

        {/* Message Body with Rich Text */}
        <div>
          <label className="label-base">Message Body</label>
          <p className="text-xs text-muted-foreground mb-1">Compose your email content. Formatting will be preserved in the final send.</p>
          {/* Toolbar */}
          <div className="flex items-center gap-1 px-2 py-1.5 border border-border border-b-0 rounded-t-lg bg-muted/40">
            {[
              { cmd: 'bold', icon: Bold, title: 'Bold' },
              { cmd: 'italic', icon: Italic, title: 'Italic' },
              { cmd: 'underline', icon: Underline, title: 'Underline' },
            ].map(({ cmd, icon: Icon, title }) => (
              <button
                key={`fmt-${cmd}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); execFormat(cmd); }}
                title={title}
                className="w-7 h-7 rounded flex items-center justify-center hover:bg-secondary transition-all duration-150"
              >
                <Icon size={14} className="text-muted-foreground" />
              </button>
            ))}
            <div className="w-px h-5 bg-border mx-1" />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); execFormat('insertUnorderedList'); }}
              title="Bullet List"
              className="w-7 h-7 rounded flex items-center justify-center hover:bg-secondary transition-all duration-150"
            >
              <List size={14} className="text-muted-foreground" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); execFormat('insertOrderedList'); }}
              title="Numbered List"
              className="w-7 h-7 rounded flex items-center justify-center hover:bg-secondary transition-all duration-150"
            >
              <ListOrdered size={14} className="text-muted-foreground" />
            </button>
            <div className="w-px h-5 bg-border mx-1" />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleInsertLink(); }}
              title="Insert Link"
              className="w-7 h-7 rounded flex items-center justify-center hover:bg-secondary transition-all duration-150"
            >
              <Link2 size={14} className="text-muted-foreground" />
            </button>
          </div>
          {/* ContentEditable editor */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => setBodyHtml((e.target as HTMLDivElement).innerHTML)}
            className="min-h-[180px] px-3 py-2.5 border border-border rounded-b-lg bg-card text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary prose prose-sm max-w-none"
            data-placeholder="Start writing your email message here…"
            style={{ lineHeight: '1.6' }}
          />
          <style>{`
            [contenteditable]:empty:before {
              content: attr(data-placeholder);
              color: var(--muted-foreground);
              pointer-events: none;
            }
          `}</style>
        </div>

        {/* File Attachments */}
        <div>
          <label className="label-base">Attachments</label>
          <p className="text-xs text-muted-foreground mb-2">Attach files up to 10MB each. Supported: PDF, images, documents.</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.csv,.xlsx"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-border rounded-lg py-4 px-4 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-150"
          >
            <Paperclip size={16} />
            Click to attach files or drag and drop
          </button>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((f) => (
                <div key={f.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 border border-border">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip size={13} className="text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{f.size}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(f.id)}
                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-red-100 transition-all duration-150 flex-shrink-0 ml-2"
                  >
                    <X size={12} className="text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tags */}
        <div>
          <label className="label-base" htmlFor="tags">Tags</label>
          <p className="text-xs text-muted-foreground mb-1">Comma-separated tags for organizing campaigns</p>
          <input
            id="tags"
            type="text"
            placeholder="e.g. newsletter, promo, product"
            className="input-base"
            {...register('tags')}
          />
        </div>

        {/* Send Timing */}
        <div>
          <label className="label-base">Send Timing</label>
          <div className="flex gap-3 mt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" value="now" {...register('sendTiming')} className="accent-primary" />
              <span className="text-sm font-medium text-foreground">Send immediately</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" value="scheduled" {...register('sendTiming')} className="accent-primary" />
              <span className="text-sm font-medium text-foreground">Schedule for later</span>
            </label>
          </div>
          {sendTiming === 'scheduled' && (
            <div className="mt-3">
              <input
                type="datetime-local"
                className="input-base"
                {...register('scheduledAt', { required: sendTiming === 'scheduled' ? 'Schedule time is required' : false })}
              />
              {errors.scheduledAt && <p className="text-xs text-red-600 mt-1">{errors.scheduledAt.message}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="btn-secondary text-sm"
            >
              <Eye size={14} /> Preview
            </button>
            <button
              type="button"
              onClick={() => setTestSendOpen(true)}
              className="btn-secondary text-sm"
            >
              <Send size={14} /> Test Send
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary min-w-[140px]">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                  </svg>
                  Creating…
                </span>
              ) : sendTiming === 'now' ? 'Create & Send' : 'Schedule Campaign'}
            </button>
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Campaign Preview" size="xl">
        <div className="p-6 space-y-4">
          {/* Email envelope header */}
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="metric-label w-20 flex-shrink-0">From:</span>
              <span className="text-foreground font-medium">{formValues.fromName || 'Your Name'} &lt;{formValues.fromEmail || 'you@domain.com'}&gt;</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="metric-label w-20 flex-shrink-0">To:</span>
              <span className="text-foreground">{formValues.mailingList || 'Selected List'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="metric-label w-20 flex-shrink-0">Subject:</span>
              <span className="text-foreground font-semibold">{formValues.subject || '(No subject)'}</span>
            </div>
          </div>

          {/* Email body preview */}
          <div className="rounded-xl border border-border bg-white overflow-hidden">
            {/* Email header bar */}
            <div className="px-6 py-4 border-b border-border/50"
              style={{ background: 'linear-gradient(135deg, rgba(91,53,232,0.06) 0%, rgba(255,92,43,0.04) 100%)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)' }}>
                  <Mail size={13} className="text-white" />
                </div>
                <span className="text-sm font-bold" style={{ color: '#5b35e8' }}>AppNest Flow</span>
              </div>
            </div>
            {/* Body */}
            <div className="px-6 py-5">
              {bodyHtml ? (
                <div
                  className="prose prose-sm max-w-none text-foreground"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              ) : (
                <p className="text-muted-foreground text-sm italic">No message body yet. Start writing in the editor to see a preview here.</p>
              )}
            </div>
            {/* Footer */}
            <div className="px-6 py-4 border-t border-border/50 bg-muted/20">
              <p className="text-xs text-muted-foreground text-center">
                You received this email because you subscribed to {formValues.mailingList || 'our list'}.{' '}
                <span className="underline cursor-pointer">Unsubscribe</span>
              </p>
            </div>
          </div>

          {/* Attachments preview */}
          {attachments.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">ATTACHMENTS ({attachments.length})</p>
              <div className="flex flex-wrap gap-2">
                {attachments.map((f) => (
                  <div key={f.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted border border-border text-xs">
                    <Paperclip size={11} className="text-muted-foreground" />
                    <span className="font-medium text-foreground">{f.name}</span>
                    <span className="text-muted-foreground">{f.size}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={() => setPreviewOpen(false)} className="btn-secondary">Close Preview</button>
          </div>
        </div>
      </Modal>

      {/* Test Send Modal */}
      <Modal open={testSendOpen} onClose={() => { setTestSendOpen(false); setTestEmail(''); setTestSent(false); }} title="Send Test Email" size="sm">
        <div className="p-6 space-y-4">
          {testSent ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-foreground">Test email sent!</p>
              <p className="text-xs text-muted-foreground text-center">Check <strong>{testEmail}</strong> for your preview email.</p>
            </div>
          ) : (
            <>
              <div>
                <p className="text-sm text-muted-foreground mb-3">
                  Send a test version of this campaign to verify how it looks in an inbox before sending to your full list.
                </p>
                <label className="label-base" htmlFor="testEmailInput">Test Email Address</label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card flex-1">
                    <Mail size={14} className="text-muted-foreground flex-shrink-0" />
                    <input
                      id="testEmailInput"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
                    />
                  </div>
                </div>
              </div>
              {/* Campaign summary */}
              <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subject:</span>
                  <span className="font-medium text-foreground truncate max-w-[200px]">{formValues.subject || '(No subject)'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">From:</span>
                  <span className="font-medium text-foreground">{formValues.fromName || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">List:</span>
                  <span className="font-medium text-foreground">{formValues.mailingList || '—'}</span>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => { setTestSendOpen(false); setTestEmail(''); }} className="btn-secondary">Cancel</button>
                <button
                  onClick={handleTestSend}
                  disabled={!testEmail || testSending}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                >
                  {testSending ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                      </svg>
                      Sending…
                    </span>
                  ) : (
                    <><Send size={14} /> Send Test</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}